import getActivePanel from '../elements/getActivePanel';
import getBackButton from '../elements/getBackButton';
import getNextButtons from '../elements/getNextButtons';
import getPanelById from '../elements/getPanelById';
import getPreviousPanel from '../elements/getPreviousPanel';
import toggleClass from '../../elements/toggleClass';
import { selectors, classes } from '../elements/constants';
import getForm from '../elements/getForm';
import * as eventsModel from './events';
import interpolatePanelText from '../text/interpolatePanelText';
import forEachElement from '../../elements/forEachElement';

export {
  getActivePanel,
  getBackButton,
  getNextButtons,
  getPanelById,
  getPreviousPanel,
  interpolatePanelText
};

/**
 * Updates target panel on the back button of the current panel
 *
 * @param {Element} currentPanel
 * @param {Element} previousPanel
 */
export const updateBackButtonTarget = (currentPanel, previousPanel) => {
  if (!currentPanel || !previousPanel) return;
  const backButton = getBackButton(currentPanel);
  if (backButton) backButton.dataset.target = previousPanel.dataset.backTargetId || previousPanel.dataset.id;
};

/**
 * Updates the classes of a panel using a simple object of key-boolean
 * @param {Element} panel
 * @param {Object} changes - An object of `{ className => true|false }` toggles
 */
export function updatePanelClasses(panel, changes) {
  if (!panel) return;
  Object.keys(changes).forEach(change => {
    toggleClass(panel, classes[change], changes[change]);
  });
}

/**
 * Visually shows a given panel, by sliding between the current and new panel.
 * @param {Element} panel - The panel you wish to show
 * @param {Object} options
 * @returns {Promise}
 */
export async function showPanel(panel, options) {
  const form = getForm(panel);
  const fromPanel = getActivePanel(form);

  if (!form || !panel) { return; }

  // deactivate the fromPanel and activate the shown panel
  return new Promise((resolve) => {
    if(fromPanel) {
      // at this point the old panel should be hidden, in whatever way it is implemented
      eventsModel.dispatchHideStepEvent(fromPanel, options);

      if(options.direction !== 'backwards') {
        // next panel's back button should point to this panel
        updateBackButtonTarget(panel, fromPanel);
      }
    }

    // set this panel as active, thus showing it visually
    updatePanelClasses(panel, { active: true, complete: false });

    // ensure view is at the top only with tutoring jobs panel
    if (document.querySelector(`.${classes.formTutoringJobs}`)) {
      window.scrollTo({top: 0, behavior: 'smooth'});
    }

    // slide panel animation takes 300ms, so resolve after that
    window.setTimeout(resolve, 300);
  });
}

/**
 * Visually hides a given panel, by sliding it away.
 * @param {Element} panel - The panel you wish to hide
 * @param {Object} options
 * @param {string} options.direction - The direction to animate in e.g. 'backward'
 */
export function hidePanel(panel, { direction } = {}) {
  if(!panel) { return; }
  updatePanelClasses(panel, { active: false, complete: direction !== 'backwards' });
}

/**
 * Automatically focus on the first input on the panel.
 * @param {Element} panel - The panel you are on
 * @param {Boolean} [invalidOnly] - True to only focus on the first invalid field
 */
export function autofocusInput(panel, invalidOnly) {
  let fieldSelectors = selectors.autoFocusFields;
  if(invalidOnly) { fieldSelectors = fieldSelectors.map(selector => `${selector}:invalid`); }
  const field = panel && panel.querySelector(fieldSelectors.join());
  if(field) { field.focus(); }
}

/**
 * Shows a loading animation to the user on the given panel, typically on the Continue button.
 * @param {Element} panel - The panel to show the loading animation on
 */
export function showLoadingAnimation(panel) {
  if(!panel) { return; }
  const nextButton = panel.querySelector(selectors.nextButton);
  if(nextButton && nextButton.classList.contains(classes.button)) {
    nextButton.classList.add(classes.buttonLoading);
  }
}

/**
 * Hides any loading animations on the given panel.
 * @param {Element} panel - The panel to stop showing the loading animation on
 */
export function hideLoadingAnimation(panel) {
  if(!panel) { return; }
  forEachElement(panel.querySelectorAll(selectors.buttonLoading), button =>
    button.classList.remove(classes.buttonLoading)
  );
}
